import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useLottie } from 'lottie-react'
import { animate, motion, useMotionValue, useTransform } from 'motion/react'

import { Button } from '../../../global/button/Button'
import { useArabicSessionLanguage } from '../../../global/context/SessionSettingsContext'
import { useMixedText } from '../../../hooks/useMixedText'
import { GiftIcon } from '../../../icons/GiftIcon'
import ConfettiLottie from '../../../lotties/ConfettiV2.json'
import {
  LoyaltyBonusStatus,
  LoyaltyDetails,
  LoyaltyTierDto,
} from '../../../model/IntroducingBrokerDetailsDto'
import { Text, TextH4, TextLarge, TextSmall, TextTiny } from '../../../ui/Typography/Typography'
import { calculatePercentage } from '../../../utils/numbers.utils'
import { ProgressBarsBlock } from '../../Dashboard/components/ProgressBarsBlock/ProgressBarsBlock'
import { LoyaltyTierMapped } from '../Loyalty'

import styles from './BigLoyaltyCard.module.scss'

interface Props {
  loyaltyTiers?: LoyaltyDetails
  onSubmitReward: (tier?: LoyaltyTierDto | LoyaltyTierMapped | null) => Promise<void>
  isLoading: boolean
}

type TierLots = {
  currentLots: number
  requiredLots: number
}

function getNonSubtierLotsForProgressBar({
  unClaimedApprovedTier,
  nextTier,
  currentTierLots,
  currentTierMinLots,
  currentTierMaxLots,
}: {
  unClaimedApprovedTier?: LoyaltyTierDto
  nextTier?: LoyaltyTierDto
  currentTierLots: number
  currentTierMinLots: number
  currentTierMaxLots: number
}): TierLots {
  let currentLots: number
  let requiredLots: number

  if (unClaimedApprovedTier) {
    currentLots = unClaimedApprovedTier.lots.current
    requiredLots = unClaimedApprovedTier.minLots
  } else if (!nextTier) {
    currentLots = currentTierLots
    requiredLots = currentTierMaxLots
  } else {
    currentLots = currentTierLots - currentTierMinLots
    requiredLots = currentTierMaxLots - currentTierMinLots
  }

  return { currentLots, requiredLots }
}

const formatLabelPercentage = (value: number): string => {
  return parseFloat(value.toFixed(2)).toString()
}

export const BigLoyaltyCard: React.FC<Props> = ({ loyaltyTiers, onSubmitReward, isLoading }) => {
  const { t } = useTranslation()
  const count = useMotionValue(0)
  const rounded = useTransform(() => formatLabelPercentage(count.get()))
  const { parseMixedText } = useMixedText()
  const isArabic = useArabicSessionLanguage()

  const currentTier = loyaltyTiers?.currentTier
  const currentTierMinLots = currentTier?.minLots ?? 0
  const currentTierMaxLots = currentTier?.maxLots ?? 0
  const currentTierLots = currentTier?.lots.current ?? 0
  const nextTier = loyaltyTiers?.nextTier

  const {
    View: Confetti,
    play: launchConfetti,
    stop: stopConfetti,
  } = useLottie({
    animationData: ConfettiLottie,
    autoplay: false,
    loop: false,
  })

  const unClaimedApprovedTier = useMemo(() => {
    return loyaltyTiers?.tiers?.find(
      (tier) => tier?.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Approved
    )
  }, [loyaltyTiers?.tiers])

  const heading = useMemo(
    () =>
      unClaimedApprovedTier || !nextTier
        ? t('IB.Dashboard.Current level')
        : t('IB.Dashboard.Next level'),
    [unClaimedApprovedTier, nextTier, t]
  )

  const lotsPercentage = useMemo(() => {
    // Use case: Bronze (4/4) is unclaimed, but sub-tiers are required to show slices of progress bar (4) instead of one. In this case look for sub-tiers level below, but only if current tier is unclaimed.
    const tierIndexWithSubTiersBeforeUnclaimedTier =
      loyaltyTiers?.tiers?.findIndex((tier, idx) => {
        const previousTier = loyaltyTiers?.tiers?.[idx - 1]

        return (
          !!tier.ibReward?.id &&
          !!unClaimedApprovedTier?.ibReward?.id &&
          tier.ibReward?.id === unClaimedApprovedTier?.ibReward?.id &&
          tier.lots.current >= previousTier?.lots.required &&
          (previousTier?.subTiers?.length ?? 0) > 0
        )
      }) ?? -1

    const tierWithSubTiersBeforeUnclaimed =
      tierIndexWithSubTiersBeforeUnclaimedTier > 0
        ? loyaltyTiers?.tiers?.[tierIndexWithSubTiersBeforeUnclaimedTier - 1]
        : undefined

    // WCA-4237 PR for extra info about workflow of this card.

    const subTiers =
      unClaimedApprovedTier?.subTiers ??
      currentTier?.subTiers ??
      tierWithSubTiersBeforeUnclaimed?.subTiers

    if (subTiers) {
      let ignoreRestCalculations = false

      return (
        subTiers?.map((tier, idx) => {
          const isUnclaimedApprovedTier =
            !!tier.ibReward?.id &&
            !!unClaimedApprovedTier?.ibReward?.id &&
            tier.ibReward.id === unClaimedApprovedTier.ibReward.id

          // If unclaimed approved tier is found, then we should ignore rest calculations and just return 0's.
          // Essentially if 1/4 is filled and it's unclaimed, then skip calculations for 2/4, etc before moving to next tier.
          if (isUnclaimedApprovedTier) {
            ignoreRestCalculations = true
          }

          let currentLots
          let requiredLots

          if (unClaimedApprovedTier || !tier.isCurrent) {
            currentLots = tier.lots.current
            requiredLots = tier?.lots.required
          } else {
            currentLots = currentTierLots - currentTierMinLots
            requiredLots = currentTierMaxLots - currentTierMinLots
          }

          const percent = !ignoreRestCalculations
            ? calculatePercentage(currentLots, requiredLots)
            : 0

          // if isCurrent - no point to calculate anything else. Same goes to if current lots are equal to required lots. We know there is no necessary data in the next tiers.
          if (tier.isCurrent || tier.lots.current === tier.lots.required) {
            ignoreRestCalculations = true
          }

          return {
            percent,
            isCurrent: tier.isCurrent,
            isNextStarted:
              calculatePercentage(
                subTiers[idx + 1]?.lots.current,
                subTiers[idx + 1]?.lots.required
              ) > 0,
          }
        }) ?? []
      )
    }

    const skipPercentageCalc =
      currentTierLots === currentTierMinLots && !unClaimedApprovedTier && nextTier

    const { currentLots, requiredLots } = getNonSubtierLotsForProgressBar({
      unClaimedApprovedTier,
      nextTier,
      currentTierLots,
      currentTierMinLots,
      currentTierMaxLots,
    })

    return [
      {
        percent: skipPercentageCalc ? 0 : calculatePercentage(currentLots, requiredLots),
        isCurrent: true,
      },
    ]
  }, [
    unClaimedApprovedTier,
    currentTier,
    loyaltyTiers?.tiers,
    currentTierLots,
    currentTierMinLots,
    currentTierMaxLots,
    nextTier,
  ])

  useEffect(() => {
    // We show 0 if amount of current lots is equal to next tier minimum lots (not applicable for unclaimed approved tier)
    const shouldSkipPercentageCalc =
      currentTierLots === currentTierMinLots && !unClaimedApprovedTier && nextTier

    const { currentLots, requiredLots } = getNonSubtierLotsForProgressBar({
      unClaimedApprovedTier,
      nextTier,
      currentTierLots,
      currentTierMinLots,
      currentTierMaxLots,
    })

    const controls = animate(
      count,
      shouldSkipPercentageCalc ? 0 : calculatePercentage(currentLots, requiredLots),
      {
        duration: 2,
      }
    )
    return () => controls.stop()
  }, [
    count,
    unClaimedApprovedTier,
    currentTierLots,
    currentTierMinLots,
    currentTierMaxLots,
    nextTier,
  ])

  const handleConfetti = () => {
    stopConfetti()
    launchConfetti()
  }

  useEffect(() => {
    if (!unClaimedApprovedTier) {
      const timeout = setTimeout(() => {
        stopConfetti()
      }, 2000)

      return () => clearTimeout(timeout)
    }

    handleConfetti()

    const interval = setInterval(() => {
      handleConfetti()
    }, 3000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unClaimedApprovedTier])

  const tierImgUri = unClaimedApprovedTier?.imageUri ?? nextTier?.imageUri ?? currentTier?.imageUri
  const tierLabel = unClaimedApprovedTier?.label ?? nextTier?.label ?? currentTier?.label

  const barCurrentLotsLabelValue = currentTierLots
  const barCurrentLotsLabel = barCurrentLotsLabelValue >= 0 ? `${barCurrentLotsLabelValue} ` : ''
  const barRequiredLotsLabelValue = unClaimedApprovedTier
    ? unClaimedApprovedTier?.minLots
    : nextTier?.minLots ?? currentTier?.minLots
  const barRequiredLotsLabel = `${barRequiredLotsLabelValue} ${t('IB.Dashboard.Lots')}`
  const barLotsLabel = `${barCurrentLotsLabel} / ${barRequiredLotsLabel}`

  return (
    <article className={styles.bigLoyaltyCard}>
      <div className={styles.confetti}>{Confetti}</div>

      <header className={styles.topSection}>
        {tierImgUri && (
          <img
            src={tierImgUri}
            alt={tierLabel || 'loyalty tier icon'}
            width={48}
            height={48}
            loading='lazy'
          />
        )}

        <div className={styles.textBox}>
          <TextLarge>{heading}</TextLarge>
          <div className={styles.title}>
            <TextH4>
              {unClaimedApprovedTier
                ? unClaimedApprovedTier?.label ?? ''
                : nextTier?.label ?? currentTier?.label ?? ''}
            </TextH4>
            <TextSmall className={styles.percentage}>
              {isArabic && parseMixedText('%')}
              <motion.span>{rounded}</motion.span>
              {!isArabic && parseMixedText('%')}
            </TextSmall>
          </div>
        </div>
      </header>

      <section className={styles.bottomSection}>
        <ProgressBarsBlock
          items={[
            {
              title: <TextTiny className={styles.lotsLabel}>{t('Campaigns.Lots')}</TextTiny>,
              label: <TextTiny className={styles.lotsLabel}>{barLotsLabel}</TextTiny>,
              sections: lotsPercentage,
            },
          ]}
          color='red'
        />

        <Button
          disabled={!unClaimedApprovedTier?.ibReward?.id || isLoading}
          size='L'
          appearance='primary'
          fullWidth
          className={classNames({
            [styles.claimButtonActive]: unClaimedApprovedTier?.ibReward?.id,
          })}
          onClick={() => {
            onSubmitReward(unClaimedApprovedTier)
          }}
          renderLeftIcon={() => <GiftIcon color='alwaysWhite' />}
        >
          <Text>{t('IB.Dashboard.Claim Rewards')}</Text>
        </Button>
      </section>
    </article>
  )
}
